import { useEffect, useState } from "react";
import AnimatedCounter from "@/components/base/AnimatedCounter";
import { heroStats } from "@/mocks/home";

const statIcons = ["ri-stack-line", "ri-calendar-2-line", "ri-water-flash-line"];

const SCENE_URL =
  "https://storage.helloreaddy.io/project_files/796c258f-5aa6-424f-aa3d-52a70118bf3b/97e40fab-2b67-4df0-8d8f-5eb917621f09_green-steel-3d.html";
const SCENE_BASE =
  "https://storage.helloreaddy.io/project_files/796c258f-5aa6-424f-aa3d-52a70118bf3b/";

// Hide the scene's built-in chrome (title card, bottom bar, labels, captions)
// and freeze the model size: stop wheel/pinch from reaching the zoom handler so
// the rotating element keeps its size (page scrolling still works normally).
const SCENE_UI_FIX =
  `<base href="${SCENE_BASE}">` +
  "<style>.title,.bar,.caption,.labels{display:none !important}</style>" +
  "<script>(function(){" +
  "var stop=function(e){e.stopPropagation();};" +
  "document.addEventListener('wheel',stop,{capture:true,passive:true});" +
  "document.addEventListener('mousewheel',stop,{capture:true,passive:true});" +
  "document.addEventListener('touchmove',function(e){if(e.touches&&e.touches.length>1){e.preventDefault();}},{capture:true,passive:false});" +
  "})();<\/script>";

const highlights = [
  { icon: "ri-fire-line", title: "Coal-free", text: "Hydrogen reduction" },
  { icon: "ri-sun-line", title: "Solar + wind", text: "PPA powered" },
  { icon: "ri-layout-grid-line", title: "Modular", text: "Scale to 200 t/day" },
];

export default function Hero() {
  const [sceneReady, setSceneReady] = useState(false);
  const [sceneSrc, setSceneSrc] = useState(SCENE_URL);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    fetch(SCENE_URL)
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error("scene fetch failed"))))
      .then((html) => {
        if (cancelled) return;
        let patched = html;
        if (/<head[^>]*>/i.test(patched)) {
          patched = patched.replace(/<head[^>]*>/i, (m) => m + SCENE_UI_FIX);
        } else if (/<html[^>]*>/i.test(patched)) {
          patched = patched.replace(/<html[^>]*>/i, (m) => m + SCENE_UI_FIX);
        } else {
          patched = SCENE_UI_FIX + patched;
        }
        objectUrl = URL.createObjectURL(new Blob([patched], { type: "text/html" }));
        setSceneSrc(objectUrl);
      })
      .catch(() => {
        /* keep the original scene URL as a safe fallback */
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  return (
    <section className="relative w-full overflow-hidden bg-background-50 pt-24 md:pt-28 lg:flex lg:min-h-[max(85vh,640px)] lg:flex-col">
      {/* One unified background spanning the whole hero */}
      <div className="pointer-events-none absolute inset-0">
        <div className="grid-texture absolute inset-0 opacity-50" />
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-background-100 to-transparent" />
        <div
          className="absolute left-1/2 top-1/2 h-[760px] w-[1200px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
          style={{ background: "oklch(var(--primary-500) / 0.07)" }}
        />
        <div
          className="absolute right-1/3 top-16 h-[420px] w-[420px] rounded-full blur-3xl"
          style={{ background: "oklch(var(--accent-500) / 0.05)" }}
        />
      </div>

      <div className="relative mx-auto flex w-full flex-1 flex-col max-w-[1600px]">
        <div className="grid flex-1 grid-cols-1 items-stretch lg:grid-cols-[1fr_1.35fr]">
          {/* Left — copy */}
          <div className="flex flex-col justify-center py-12 pl-6 pr-6 md:pl-10 md:pr-10 lg:py-10 lg:pl-16 lg:pr-12">
            <div className="max-w-xl">
              <div className="animate-fade-up flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2.5 rounded-full border border-foreground-950/10 bg-background-50/70 px-3.5 py-1.5 backdrop-blur">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-accent-500" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-500" />
                  </span>
                  <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-foreground-700">
                    50 t/day green iron pilot
                  </span>
                </span>
                <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-foreground-500">
                  Andhra Pradesh · 2026
                </span>
              </div>

              <h1
                className="animate-fade-up mt-5 font-heading text-3xl font-light leading-[1.05] tracking-[-0.03em] text-foreground-950 sm:text-4xl lg:text-5xl xl:text-[3.4rem]"
                style={{ animationDelay: "0.08s" }}
              >
                The green steel
                <span className="block">
                  <span className="font-editorial font-normal italic text-primary-600">from green hydrogen.</span>
                </span>
              </h1>

              <div
                className="animate-fade-up mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-accent-500 to-primary-500"
                style={{ animationDelay: "0.12s" }}
              />

              <p
                className="animate-fade-up mt-5 max-w-lg text-sm font-light leading-relaxed text-foreground-600 md:text-base"
                style={{ animationDelay: "0.16s" }}
              >
                DJT EV Power Tech is building a modular pilot that produces 50 tonnes of green iron (HBI) a day.
                Water and renewable power go in; near-zero-carbon iron comes out, with steelmaking joining in
                phase&nbsp;2.
              </p>

              <div
                className="animate-fade-up mt-6 flex flex-wrap gap-3"
                style={{ animationDelay: "0.2s" }}
              >
                {highlights.map((item) => (
                  <div
                    key={item.title}
                    className="flex items-center gap-3 rounded-2xl border border-foreground-950/10 bg-background-50/60 px-3.5 py-2.5 backdrop-blur transition-colors hover:border-primary-300"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <div className="leading-tight">
                      <div className="font-heading text-[13px] font-semibold text-foreground-900">{item.title}</div>
                      <div className="text-[11px] text-foreground-600">{item.text}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div
                className="animate-fade-up mt-6 flex flex-col gap-3 sm:flex-row sm:items-center"
                style={{ animationDelay: "0.24s" }}
              >
                <a
                  href="#products"
                  className="group inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-full bg-primary-600 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
                >
                  Explore green HBI
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <a
                  href="#process"
                  className="group inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-full border border-foreground-950/15 px-7 py-3 text-sm font-semibold text-foreground-900 transition-colors hover:border-foreground-950/30 hover:bg-foreground-950/[0.04]"
                >
                  <i className="ri-play-circle-line text-lg transition-transform duration-300 group-hover:scale-110" />
                  How it works
                </a>
              </div>
            </div>
          </div>

          {/* Right — video on top, stat cards below, video keyed into the page background */}
          <div className="flex w-full flex-col gap-4 py-4 lg:py-8">
            {/* Video area */}
            <div className="relative h-[340px] w-full overflow-hidden sm:h-[480px] lg:h-auto lg:min-h-[420px] lg:flex-1">
              {/* Live 3D scene — its own background is lifted and blended away into the page */}
              <iframe
                src={sceneSrc}
                title="Interactive 3D green steel plant"
                className={`absolute inset-0 h-full w-full border-0 mix-blend-multiply transition-opacity duration-700 ${
                  sceneReady ? "opacity-100" : "opacity-0"
                }`}
                allow="autoplay; fullscreen"
                aria-label="Interactive 3D model of a green steel plant"
                style={{
                  transform: "translateY(-2%) scale(0.98)",
                  transformOrigin: "center center",
                  filter: "brightness(1.3) saturate(1.22) contrast(1.04)",
                }}
                onLoad={() => window.setTimeout(() => setSceneReady(true), 900)}
              />

              {/* Loader */}
              <div
                className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center transition-opacity duration-500 ${
                  sceneReady ? "opacity-0" : "opacity-100"
                }`}
                aria-hidden="true"
              >
                <i className="ri-loader-4-line animate-spin text-3xl text-primary-600" />
              </div>
            </div>

            {/* Stat cards */}
            <div className="px-6 md:px-10 lg:pl-0 lg:pr-10">
              <div
                className="animate-fade-up grid grid-cols-1 divide-y divide-foreground-950/10 overflow-hidden rounded-2xl border border-foreground-950/10 bg-background-100/60 sm:grid-cols-3 sm:divide-x sm:divide-y-0"
                style={{ animationDelay: "0.32s" }}
              >
                {heroStats.map((stat, i) => (
                  <div key={stat.label} className="px-4 py-4 transition-colors hover:bg-background-50/70">
                    <span className="mb-2.5 flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <i className={`${statIcons[i]} text-base`} />
                    </span>
                    <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                      <span className="font-heading text-2xl font-light tracking-[-0.02em] text-foreground-950">
                        <AnimatedCounter value={stat.value} decimals={stat.decimals} />
                      </span>
                      <span className="font-heading text-sm font-medium text-primary-600">{stat.unit}</span>
                    </div>
                    <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-foreground-500 md:text-[11px]">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}